import { useCallback } from 'react'

import { useMutation } from '@apollo/client/react'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import type { IdeaContent } from '~publish/pages/Create/types'

import { UNASSIGNED_GROUP_ID } from '../../pages/Create/components/Board/helpers/helpers'
import { QUERY_IDEAS_COUNT } from '../../pages/Create/hooks/useIdeasCount'

export const LEGACY_CREATE_IDEA = graphql(/* GraphQL */ `
  mutation LegacyCreateIdea($input: CreateIdeaInput!) {
    createIdea(input: $input) {
      __typename
      ... on Idea {
        id
        content {
          title
          text
          aiAssisted
          media {
            id
            url
            alt
            thumbnailUrl
            type
            size
            source {
              name
              id
            }
          }
          tags {
            id
            name
            color
          }
        }
        organizationId
        groupId
        createdAt
      }
      ... on MutationError {
        message
      }
    }
  }
`)

export type LegacyCreateIdeaMutation = ResultOf<typeof LEGACY_CREATE_IDEA>
type LegacyCreateIdeaMutationVariables = VariablesOf<typeof LEGACY_CREATE_IDEA>

type CreateIdeaArgs = {
  content: IdeaContent
  source?: CTA
  groupId?: string
  placeAfterId?: string
}

type CreateIdeaCompleted = NonNullable<
  useMutation.Options<
    LegacyCreateIdeaMutation,
    LegacyCreateIdeaMutationVariables
  >['onCompleted']
>

type CreateIdeaError = NonNullable<
  useMutation.Options<
    LegacyCreateIdeaMutation,
    LegacyCreateIdeaMutationVariables
  >['onError']
>

export function useCreateIdea({
  onCompleted,
  onError,
}: {
  onCompleted?: CreateIdeaCompleted
  onError?: CreateIdeaError
} = {}): {
  createIdea: (args: CreateIdeaArgs) => void
  createIdeaData: useMutation.Result<LegacyCreateIdeaMutation>['data']
  createIdeaError: useMutation.Result<LegacyCreateIdeaMutation>['error']
  createIdeaLoading: useMutation.Result<LegacyCreateIdeaMutation>['loading']
} {
  const organizationId = useOrganizationId()

  const [
    executeCreateIdea,
    {
      data: createIdeaData,
      loading: createIdeaLoading,
      error: createIdeaError,
    },
  ] = useMutation(LEGACY_CREATE_IDEA, {
    refetchQueries: [
      QUERY_IDEAS_COUNT,
      'GetIdeasBoard',
      'GetIdeasGallery',
      'GetIdeasList',
    ],
    onCompleted,
    onError,
  })

  const createIdea = useCallback(
    ({ content, groupId, placeAfterId, source }: CreateIdeaArgs) => {
      if (organizationId) {
        executeCreateIdea({
          variables: {
            input: {
              organizationId,
              cta: source ?? 'create-webapp-createIdea-default-1',
              content,
              group: groupId
                ? {
                    groupId: groupId === UNASSIGNED_GROUP_ID ? null : groupId,
                    placeAfterId,
                  }
                : undefined,
            },
          },
        })
      }
    },
    [executeCreateIdea, organizationId],
  )

  return { createIdea, createIdeaData, createIdeaError, createIdeaLoading }
}
